import { RADIUS, Turnstile, WithHalfdot } from "@elyxeer/ui";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useRef,
  useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Link as RouterLink,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useLocale } from "~/i18n";
import { requestOtp } from "~/lib/api";
import { KEYBOARD_INSET_VAR } from "~/lib/keyboard-inset";
import { legalPath } from "~/lib/legal";
import { turnstileBridgeUrl } from "~/lib/turnstile";
import { useAuthStore } from "~/store/auth-store";

import {
  AUTH_CODE_PATH,
  type CodeStepState,
  translateAuthError,
} from "./auth-flow";
import { CodeField } from "./CodeField";

/**
 * Step two of passwordless sign-in: the code that arrived by mail.
 *
 * A screen of its own rather than a second face of the sign-in drawer. The pitch and
 * the footage have done their work by now; what is left is one field, and it gets the
 * whole surface — with the keypad up, a drawer over a video had nowhere to put either
 * the boxes or the button.
 *
 * It is written twice over. `otp/request` tells us whether verifying will create the
 * account, and someone signing up needs to be told that — and must explicitly accept the terms
 * before continuing. A returning member gets neither.
 */

/**
 * The one failure another code cannot answer: the address is out of requests for the
 * window, and asking again would only spend the member's patience on a second 429.
 */
const RATE_LIMITED_KEY = "auth.errors.rateLimited";

/**
 * How far a resend has got. `captcha` is the wait for Turnstile — `otp/request` is
 * captcha-gated, and the token the sign-in screen minted was spent by the request that
 * produced this screen, so a resend has to earn one of its own.
 */
type ResendState = "idle" | "captcha" | "sending" | "sent";

/**
 * One link inside the terms notice.
 *
 * An in-app route rather than the marketing site: this is the moment the member is
 * asked to accept, so what they are accepting has to open without leaving the flow —
 * and, in the native shell, without a connection. Back from there is a history pop,
 * which returns to this screen with the address and the sign-up flag it is holding in
 * location state intact. An `<a>` does not submit the form it sits inside.
 */
function LegalLink({ to, children }: { to: string; children?: ReactNode }) {
  return (
    <Link
      component={RouterLink}
      to={to}
      variant="T3"
      underline="always"
      sx={{ color: "inherit" }}
    >
      {children}
    </Link>
  );
}

export function VerifyScreen() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { state } = useLocation() as { state: CodeStepState | null };
  const verify = useAuthStore((store) => store.verify);
  const config = useAuthStore((store) => store.config);

  const [code, setCode] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resend, setResend] = useState<ResendState>("idle");
  // Bumping this remounts the widget, which is the only way to get a new token —
  // Turnstile tokens are single-use and a request spends the one it carried.
  const [captchaNonce, setCaptchaNonce] = useState(0);
  // Single-flight guard. A widget that refreshes itself calls back a second time, and
  // two requests would burn two of the address's five slots for the hour — and leave
  // the member reading a code that the later request has already invalidated.
  const sending = useRef(false);

  const codeLength = config?.code_length ?? 6;
  const email = state?.email ?? "";
  const signingUp = state?.signingUp ?? false;
  const returnTo = state?.returnTo;
  const captchaRequired = config?.captcha_required ?? false;

  const submitCode = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || code.length < codeLength || (signingUp && !termsAccepted))
      return;
    setBusy(true);
    setErrorKey(null);
    try {
      await verify(email, code);
      if (returnTo) navigate(returnTo, { replace: true });
    } catch (error) {
      setErrorKey(translateAuthError(error));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Ask for another code without leaving the screen.
   *
   * A code lives ten minutes, and the only way to replace an expired one used to be
   * the back arrow and the whole address step again. `consume_code` matches nothing
   * but the newest live code, so the boxes are cleared along with it: whatever is
   * typed in them cannot verify any more, and the dev banner would be lying too.
   */
  const sendNewCode = useCallback(
    async (token: string | null) => {
      if (sending.current) return;
      sending.current = true;
      setResend("sending");
      try {
        const response = await requestOtp(email, {
          captchaToken: token ?? undefined,
          locale,
        });
        setCode("");
        setErrorKey(null);
        setResend("sent");
        // Replaced rather than pushed: this is the same step, carrying what the new
        // request answered. Back still goes to the address, not to a stale code.
        navigate(AUTH_CODE_PATH, {
          replace: true,
          state: {
            email,
            signingUp: response.is_new_user,
            debugCode: response.debug_code,
            returnTo,
          } satisfies CodeStepState,
        });
      } catch (error) {
        setErrorKey(translateAuthError(error));
        setResend("idle");
      } finally {
        sending.current = false;
        // The token is spent either way, so the next attempt starts a fresh widget.
        setCaptchaNonce((nonce) => nonce + 1);
      }
    },
    [email, locale, navigate, returnTo],
  );

  // The widget solves itself once mounted; the token arriving is the go-ahead. Stable
  // as long as the address is, because `Turnstile` remounts — and so restarts the
  // challenge — whenever this identity changes.
  const handleCaptcha = useCallback(
    (token: string | null) => {
      if (token) void sendNewCode(token);
    },
    [sendNewCode],
  );

  const startResend = () => {
    if (captchaRequired) {
      setResend("captcha");
      return;
    }
    void sendNewCode(null);
  };

  // Reloading the app drops the router state, and a code without the request that
  // produced it is nothing to look at. Start the flow again rather than show a form
  // that cannot be submitted.
  if (!email) return <Navigate to="/auth" replace />;

  return (
    <Box
      component="form"
      onSubmit={submitCode}
      sx={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "background.paper",
        paddingInline: 3,
        // Rendered outside the app shell, so it carries its own safe-area insets. The
        // keyboard's share comes off the bottom, which is what keeps the docked button
        // above the keys instead of behind them.
        paddingTop: "calc(12px + env(safe-area-inset-top))",
        paddingBottom: `calc(24px + env(safe-area-inset-bottom) + var(${KEYBOARD_INSET_VAR}, 0px))`,
      }}
    >
      {/* Phone: the column is the screen, and the action docks to its floor. Desktop:
          it is a centred measure of the same width as the sign-in card. */}
      <Box
        sx={{
          width: "100%",
          maxWidth: { md: 460 },
          marginInline: { md: "auto" },
          marginBlock: { md: "auto" },
          flex: { xs: 1, md: "0 1 auto" },
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* Pulled out of its own padding so the arrow, not the tap target around
            it, lines up with the title below. */}
          <IconButton
            aria-label={t("common.back")}
            onClick={() => navigate(-1)}
            disabled={busy}
            data-testid="auth.back"
            sx={{ color: "text.secondary", marginLeft: -1, flexShrink: 0 }}
          >
            <ArrowBackIcon />
          </IconButton>

          {/*
          Development only, and it rides in the header row rather than above the form:
          the row is there either way, so the screen a developer sees with
          `OTP_ECHO_CODE=true` is laid out exactly like the one that ships.
        */}
          {state?.debugCode ? (
            <Box
              data-testid="auth.debug-code"
              sx={{
                flex: 1,
                minWidth: 0,
                paddingInline: 1.5,
                paddingBlock: 0.5,
                borderRadius: `${RADIUS.full}px`,
                backgroundColor: "background.default",
                border: "1px solid",
                borderColor: "divider",
                textAlign: "center",
              }}
            >
              <Typography
                variant="L3"
                sx={{ color: "text.muted", display: "block" }}
              >
                {t("auth.devCodeHint", { code: state.debugCode })}
              </Typography>
            </Box>
          ) : null}
        </Box>

        <Box>
          <Typography variant="H1" sx={{ color: "text.primary" }}>
            <WithHalfdot size={12}>
              {t(signingUp ? "auth.signUpTitle" : "auth.signInTitle")}
            </WithHalfdot>
          </Typography>
          <Typography
            variant="T2"
            sx={{ color: "text.secondary", marginTop: 1.5 }}
          >
            {t(signingUp ? "auth.signUpSubtitle" : "auth.signInSubtitle", {
              email,
            })}
          </Typography>
        </Box>

        <CodeField
          value={code}
          onChange={setCode}
          length={codeLength}
          label={t("auth.codeLabel")}
          error={errorKey !== null}
          autoFocus
          testId="auth.code"
        />

        {errorKey ? (
          <Alert severity="error" data-testid="auth.error">
            {t(errorKey)}{" "}
            {/* The way out of an expired code, in the sentence that reports it: every
                failure here is worth another code except the one that says the address
                has already asked for too many. */}
            {errorKey === RATE_LIMITED_KEY ? null : (
              <Link
                component="button"
                type="button"
                variant="T2"
                underline="always"
                disabled={busy || resend !== "idle"}
                onClick={startResend}
                data-testid="auth.resend"
                sx={{ color: "inherit", "&:disabled": { cursor: "default" } }}
              >
                {t(
                  resend === "idle" ? "auth.resendCode" : "auth.resendingCode",
                )}
              </Link>
            )}
          </Alert>
        ) : resend === "sent" ? (
          <Alert severity="success" data-testid="auth.resent">
            {t("auth.codeResent", { email })}
          </Alert>
        ) : null}

        {/* Mounted only for the wait it covers, so a member who never resends never
            loads Cloudflare's script on this screen at all. */}
        {resend === "captcha" ? (
          <Turnstile
            key={captchaNonce}
            onToken={handleCaptcha}
            bridgeUrl={turnstileBridgeUrl()}
          />
        ) : null}

        {/* Docked: the action sits on the floor of whatever the keyboard leaves. */}
        <Box
          sx={{
            marginTop: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {signingUp ? (
            // `required` makes MUI wrap the label and its asterisk in a bare <div>, so
            // the label has to be inline for the asterisk to follow the last word, and
            // the checkbox-aligning offset lives on the row, where it moves both.
            <FormControlLabel
              sx={{
                margin: 0,
                paddingTop: 1.25,
                alignItems: "flex-start",
                gap: 0.5,
              }}
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(_, checked) => setTermsAccepted(checked)}
                  disabled={busy}
                  required
                  slotProps={{
                    input: { ...{ "data-testid": "auth.accept-terms" } },
                  }}
                  sx={{ padding: 1.25, marginLeft: -1.25, marginTop: -1.25 }}
                />
              }
              label={
                <Typography
                  variant="T3"
                  component="span"
                  sx={{ color: "text.secondary" }}
                  data-testid="auth.legal"
                >
                  <Trans
                    i18nKey="auth.legal"
                    components={{
                      terms: <LegalLink to={legalPath("terms")} />,
                      privacy: <LegalLink to={legalPath("privacy")} />,
                    }}
                  />
                </Typography>
              }
            />
          ) : null}
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={
              busy || code.length < codeLength || (signingUp && !termsAccepted)
            }
            data-testid="auth.verify"
          >
            {t("common.continue")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
